/**
 * GhlConnectPanel – LeadConnector CRM integration for the marketing-analyst module.
 * Connect LeadConnector → fetch appointments (booked calls) + opportunities (sales).
 * Data is displayed in the shared KPI dashboard and passed to LLM context.
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { escapeHtml } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  CalendarCheck,
  Trophy,
  ChevronDown,
  ChevronUp,
  XCircle,
  Users,
  Tag,
} from "lucide-react";

interface GhlLocationInfo {
  id: string;
  locationId: string;
  locationName?: string;
  companyId?: string;
  tokenExpiresAt?: string;
}

interface GhlStatus {
  connected: boolean;
  locationId?: string;
  locationName?: string;
  companyId?: string;
  tokenExpiresAt?: string;
  /** All linked subaccounts; for non-multi users this is a 1-element array. */
  locations?: GhlLocationInfo[];
  /** True when the user is allowed to connect more than one subaccount. */
  multiAccountAllowed?: boolean;
}

export interface GhlSalesData {
  totalAppointments: number;
  confirmedAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  noShowAppointments: number;
  totalOpportunities: number;
  wonOpportunities: number;
  lostOpportunities: number;
  openOpportunities: number;
  totalRevenue: number;
  totalContacts: number;
  contactsByTag: Record<string, number>;
  contactsBySource: Record<string, number>;
  appointments: unknown[];
  opportunities: Array<{
    id: string;
    name?: string;
    status?: string;
    monetaryValue?: number;
  }>;
  contacts: unknown[];
  context: string;
  locationId: string;
  locationName: string;
  dateFrom: string;
  dateTo: string;
}

interface GhlConnectPanelProps {
  onDataLoaded?: (context: string, data: GhlSalesData) => void;
  dateFrom?: string;
  dateTo?: string;
  forUserId?: string;
  attributionSource?: "ghl" | "tracking_events";
}

// LeadConnector Logo
const GhlLogo = () => (
  <div className="w-6 h-6 rounded bg-orange-500 flex items-center justify-center">
    <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
      <path
        d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

export default function GhlConnectPanel({
  onDataLoaded,
  dateFrom,
  dateTo,
  forUserId,
  attributionSource,
}: GhlConnectPanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [salesData, setSalesData] = useState<GhlSalesData | null>(null);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  // Check LeadConnector connection status
  const { data: status, isLoading: statusLoading } = useQuery<GhlStatus>({
    queryKey: ["ghl-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/crm/status${fuParam}`),
    staleTime: 30_000,
  });

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "GHL_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({ queryKey: ["ghl-integration-status"] });
      }
      if (event.data?.type === "GHL_OAUTH_ERROR") {
        // eslint-disable-next-line no-console -- front nie ma loggera; celowe logowanie diagnostyczne
        console.error("[LeadConnector] OAuth error:", event.data.error);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient]);

  // Connect mutation – open blank popup synchronously to avoid popup blocker
  const popupRef = useRef<Window | null>(null);
  const connectMutation = useMutation({
    mutationFn: () => apiRequest("GET", `/api/integrations/crm/connect${fuParam}`),
    onSuccess: (data: { url: string }) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.location.href = data.url;
      }
    },
    onError: (err) => {
      // Show error in popup instead of silently closing
      if (popupRef.current && !popupRef.current.closed) {
        try {
          popupRef.current.document.body.innerHTML = `<div style="padding:20px;font-family:sans-serif;color:#333"><h3>${escapeHtml(t("ghl.popup.errorTitle"))}</h3><p>${escapeHtml(t("ghl.popup.errorBody"))}</p></div>`;
        } catch {
          popupRef.current.close();
        }
      }
    },
  });
  const handleGhlConnect = () => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    popupRef.current = window.open(
      "about:blank",
      "ghl_oauth",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    // Write loading page immediately so browser doesn't auto-close blank popup
    if (popupRef.current) {
      popupRef.current.document.write(
        `<html><head><title>LeadConnector</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>${escapeHtml(t("ghl.popup.loading"))}</p></body></html>`
      );
      popupRef.current.document.close();
    }
    const checkInterval = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(checkInterval);
        queryClient.invalidateQueries({ queryKey: ["ghl-integration-status"] });
      }
    }, 500);
    connectMutation.mutate();
  };

  // Disconnect mutation – optionally targets a single subaccount via locationId.
  const disconnectMutation = useMutation({
    mutationFn: (vars?: { locationId?: string }) => {
      const body: Record<string, string> = {};
      if (forUserId) body.forUserId = forUserId;
      if (vars?.locationId) body.locationId = vars.locationId;
      return apiRequest(
        "POST",
        "/api/integrations/crm/disconnect",
        Object.keys(body).length ? body : undefined
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ghl-integration-status"] });
      setSalesData(null);
    },
  });

  // Fetch sales data mutation
  const salesMutation = useMutation({
    mutationFn: (params: { dateFrom: string; dateTo: string }) =>
      apiRequest("POST", "/api/integrations/crm/sales-data", {
        ...params,
        attributionSource,
        ...(forUserId ? { forUserId } : {}),
      }),
    onSuccess: (data: GhlSalesData) => {
      setSalesData(data);
      onDataLoaded?.(data.context, data);
    },
  });

  if (statusLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-orange-400" />
        <span className="text-sm text-white/60">{t("ghl.checkingConnection")}</span>
      </div>
    );
  }

  // ----- NOT CONNECTED -----
  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GhlLogo />
            <span className="text-sm font-medium text-white">LeadConnector</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("ghl.notConnected")}
            </Badge>
          </div>
          <Button
            size="sm"
            onClick={handleGhlConnect}
            disabled={connectMutation.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("ghl.connecting")}
              </>
            ) : (
              t("ghl.connect")
            )}
          </Button>
        </div>
        <p className="text-xs text-white/40">{t("ghl.connectHint")}</p>
      </div>
    );
  }

  // ----- CONNECTED -----
  const locations = status.locations ?? [];
  const isMultiAccount = !!status.multiAccountAllowed;
  // Show the per-subaccount list when the user is in the multi-GHL allowlist.
  // Single-integration users keep the compact "Connected · X | Disconnect" layout.
  const showLocationList = isMultiAccount;

  return (
    <div className="p-3 bg-white/[0.04] border border-orange-500/30 rounded-lg space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GhlLogo />
          <span className="text-sm font-medium text-white">LeadConnector</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {showLocationList && locations.length > 1
              ? t("ghl.connectedCount", { count: locations.length })
              : t("ghl.connectedTo", { name: status.locationName || status.locationId })}
          </Badge>
        </div>
        {!showLocationList && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => disconnectMutation.mutate(undefined)}
            disabled={disconnectMutation.isPending}
            className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            {t("ghl.disconnect")}
          </Button>
        )}
      </div>

      {/* Multi-subaccount list */}
      {showLocationList && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase text-white/40 tracking-wide">
            {t("ghl.subaccountsTitle")}
          </div>
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-md px-2.5 py-1.5"
            >
              <span className="text-xs text-white/80 truncate">
                {loc.locationName || loc.locationId}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => disconnectMutation.mutate({ locationId: loc.locationId })}
                disabled={disconnectMutation.isPending}
                className="h-6 text-[11px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                {t("ghl.disconnectThis")}
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            onClick={handleGhlConnect}
            disabled={connectMutation.isPending}
            className="w-full h-7 text-[11px] bg-orange-600/80 hover:bg-orange-600 text-white"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("ghl.connecting")}
              </>
            ) : (
              t("ghl.connectAnother")
            )}
          </Button>
        </div>
      )}

      {/* Fetch button */}
      {dateFrom && dateTo && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => salesMutation.mutate({ dateFrom, dateTo })}
            disabled={salesMutation.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
          >
            {salesMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("ghl.fetching")}
              </>
            ) : (
              t("ghl.fetchData")
            )}
          </Button>
          <span className="text-[10px] text-white/40">
            {dateFrom} – {dateTo}
          </span>
        </div>
      )}

      {!dateFrom && <p className="text-xs text-white/40">{t("ghl.noDateRangeHint")}</p>}

      {/* LeadConnector Data Summary */}
      {salesData && <GhlDataSummary data={salesData} />}

      {/* Status */}
      {salesData && (
        <div className="flex items-center gap-2">
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("ghl.dataLoaded")}
          </Badge>
          <span className="text-xs text-white/40">{t("ghl.dataLoadedHint")}</span>
        </div>
      )}

      {salesMutation.isError && <p className="text-xs text-amber-400/70">{t("ghl.retryHint")}</p>}
    </div>
  );
}

// ---- LeadConnector Data Mini Summary ----
function GhlDataSummary({ data }: { data: GhlSalesData }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const currency = i18n.language === "pl" ? "PLN" : "USD";
  const fmtCur = (v: number) =>
    new Intl.NumberFormat(locale, { style: "currency", currency }).format(v);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-2">
      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2">
          <div className="flex items-center gap-1.5">
            <Users className="h-3 w-3 text-blue-400" />
            <span className="text-[10px] text-white/60 uppercase">{t("ghl.summary.contacts")}</span>
          </div>
          <div className="text-lg font-bold text-blue-400 mt-0.5">{data.totalContacts}</div>
          <div className="text-[10px] text-white/40">
            {t("ghl.summary.tagsCount", { count: Object.keys(data.contactsByTag || {}).length })}
          </div>
        </div>

        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2">
          <div className="flex items-center gap-1.5">
            <CalendarCheck className="h-3 w-3 text-orange-400" />
            <span className="text-[10px] text-white/60 uppercase">
              {t("ghl.summary.appointments")}
            </span>
          </div>
          <div className="text-lg font-bold text-orange-400 mt-0.5">{data.totalAppointments}</div>
          <div className="text-[10px] text-white/40">
            {t("ghl.summary.completedNoShow", {
              completed: data.completedAppointments,
              noShow: data.noShowAppointments,
            })}
          </div>
        </div>

        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2">
          <div className="flex items-center gap-1.5">
            <Trophy className="h-3 w-3 text-emerald-400" />
            <span className="text-[10px] text-white/60 uppercase">{t("ghl.summary.sales")}</span>
          </div>
          <div className="text-lg font-bold text-emerald-400 mt-0.5">{data.wonOpportunities}</div>
          <div className="text-[10px] text-white/40">
            {t("ghl.summary.outOf", { total: data.totalOpportunities })}
          </div>
        </div>

        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-white/60 uppercase">{t("ghl.summary.revenue")}</span>
          </div>
          <div className="text-lg font-bold text-cyan-400 mt-0.5">{fmtCur(data.totalRevenue)}</div>
          <div className="text-[10px] text-white/40">{t("ghl.summary.fromWon")}</div>
        </div>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-white/60 uppercase">
              {t("ghl.summary.closeRate")}
            </span>
          </div>
          <div className="text-lg font-bold text-red-400 mt-0.5">
            {data.totalAppointments > 0
              ? `${((data.wonOpportunities / data.totalAppointments) * 100).toFixed(1)}%`
              : "—"}
          </div>
          <div className="text-[10px] text-white/40">{t("ghl.summary.closeRateHint")}</div>
        </div>
      </div>

      {/* Expand details */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors"
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        <span className="font-medium">
          {t("ghl.detailsSummary", {
            contacts: data.totalContacts,
            appointments: data.totalAppointments,
            opportunities: data.totalOpportunities,
          })}
        </span>
      </button>

      {expanded && (
        <div className="space-y-2">
          {/* Tags breakdown */}
          {data.contactsByTag && Object.keys(data.contactsByTag).length > 0 && (
            <div className="rounded-lg border border-red-500/20 bg-blue-500/5 p-2 text-xs space-y-1">
              <div className="flex items-center gap-1.5 font-medium text-blue-300 mb-1">
                <Tag className="h-3 w-3" /> {t("ghl.tagsTitle")}
              </div>
              {Object.entries(data.contactsByTag).map(([tag, count]) => (
                <div key={tag} className="flex justify-between py-0.5">
                  <span className="text-white/70 truncate max-w-[250px]">{tag}</span>
                  <span className="text-blue-400 font-medium ml-2">{count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Source breakdown */}
          {data.contactsBySource && Object.keys(data.contactsBySource).length > 0 && (
            <div className="rounded-lg border border-white/[0.08] p-2 text-xs space-y-1">
              <div className="font-medium text-white/70 mb-1">{t("ghl.sourceTitle")}</div>
              {Object.entries(data.contactsBySource).map(([source, count]) => (
                <div key={source} className="flex justify-between py-0.5">
                  <span className="text-white/70 truncate max-w-[250px]">{source}</span>
                  <span className="text-white font-medium ml-2">{count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Appointments breakdown */}
          <div className="rounded-lg border border-white/[0.08] p-2 text-xs space-y-1">
            <div className="font-medium text-white/70 mb-1">{t("ghl.appointmentsTitle")}</div>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-white/60">{t("ghl.appt.confirmed")}:</span>
              <span className="text-white">{data.confirmedAppointments}</span>
              <span className="text-white/60">{t("ghl.appt.completed")}:</span>
              <span className="text-emerald-400">{data.completedAppointments}</span>
              <span className="text-white/60">{t("ghl.appt.cancelled")}:</span>
              <span className="text-red-400">{data.cancelledAppointments}</span>
              <span className="text-white/60">{t("ghl.appt.noShow")}:</span>
              <span className="text-amber-400">{data.noShowAppointments}</span>
            </div>
          </div>

          {/* Opportunities breakdown */}
          <div className="rounded-lg border border-white/[0.08] p-2 text-xs space-y-1">
            <div className="font-medium text-white/70 mb-1">{t("ghl.opportunitiesTitle")}</div>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-white/60">{t("ghl.opp.open")}:</span>
              <span className="text-blue-400">{data.openOpportunities}</span>
              <span className="text-white/60">{t("ghl.opp.won")}:</span>
              <span className="text-emerald-400">{data.wonOpportunities}</span>
              <span className="text-white/60">{t("ghl.opp.lost")}:</span>
              <span className="text-red-400">{data.lostOpportunities}</span>
            </div>
          </div>

          {/* Won deals list */}
          {data.opportunities.filter((o) => o.status === "won").length > 0 && (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2 text-xs">
              <div className="font-medium text-emerald-400 mb-1">{t("ghl.wonDealsTitle")}</div>
              {data.opportunities
                .filter((o) => o.status === "won")
                .map((o) => (
                  <div key={o.id} className="flex justify-between py-0.5">
                    <span className="text-white truncate max-w-[200px]">{o.name}</span>
                    <span className="text-emerald-400 font-medium ml-2">
                      {fmtCur(o.monetaryValue || 0)}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
